import React from "react";
import PeopleFilterDropdown from "../filters/PeopleFilterDropdown";
import PhotoPermissionsDropdown from "../filters/PhotoPermissionsDropdown";
import DateRangeDropdown from "../filters/DateRangeDropdown";
import EventFilterDropdown from "../filters/EventFilterDropdown";
import TagFilterDropdown from "../filters/TagFilterDropdown";
import greySelfieIcon from "../style/grey-selfie.svg";
import { SearchFilters } from "../filters/filterTypes";

type ActiveDropdown =
  | "permissions"
  | "date"
  | "event"
  | "tags"
  | "people"
  | "selfie"
  | null;

interface MobileFiltersModalProps {
  isOpen: boolean;
  onClose: () => void;
  filters: SearchFilters;
  activeDropdown: ActiveDropdown;
  toggleDropdown: (dropdown: ActiveDropdown) => void;
  applyFilter: (key: keyof SearchFilters, value: any, context?: any) => void;
  allPeople: Array<{ id: number; name: string; photo_count: number }>;
  allEvents: Array<{ id: number; name: string; photo_count: number }>;
  allTags: Array<{ id: number; name: string; photo_count: number }>;
  availablePermissionAttributes: Array<{
    attributeId: number;
    attributeLabel: string;
  }>;
  showPermissionsFilter: boolean;
  showPeopleFilter: boolean;
  hasRedactedParticipants: boolean;
  isLoading: boolean;
  loadingItemId: number | null;
  loadingPermissionKey: string | null;
  activeFilterCount: number;
  peopleActive: boolean;
  permissionsActive: boolean;
  dateActive: boolean;
  selfieActive: boolean;
  eventActive: boolean;
  tagActive: boolean;
}

const MobileFiltersModal: React.FC<MobileFiltersModalProps> = ({
  isOpen,
  onClose,
  filters,
  activeDropdown,
  toggleDropdown,
  applyFilter,
  allPeople,
  allEvents,
  allTags,
  availablePermissionAttributes,
  showPermissionsFilter,
  showPeopleFilter,
  hasRedactedParticipants,
  isLoading,
  loadingItemId,
  loadingPermissionKey,
  activeFilterCount,
  peopleActive,
  permissionsActive,
  dateActive,
  selfieActive,
  eventActive,
  tagActive
}) => {
  if (!isOpen) return null;

  return (
    <div className="mobile-filters-overlay" onClick={onClose}>
      <div className="mobile-filters-modal" onClick={e => e.stopPropagation()}>
        <div className="mobile-filters-header">
          <h3>Select Filters</h3>
          <button
            type="button"
            className="mobile-filters-close"
            onClick={onClose}
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <div className="mobile-filters-content">
          {showPeopleFilter && (
          <div className="mobile-filter-section">
            <button
              type="button"
              className={`mobile-filter-toggle${
                peopleActive ? " mobile-filter-toggle--active" : ""
              }`}
              onClick={e => {
                e.stopPropagation();
                toggleDropdown(activeDropdown === "people" ? null : "people");
              }}
            >
              <span className="mobile-filter-toggle-label">
                PEOPLE
                {peopleActive && (
                  <span className="mobile-filter-badge">
                    {filters.selectedPersonIds.length}
                  </span>
                )}
              </span>
              <i
                className={`fa fa-chevron-${
                  activeDropdown === "people" ? "up" : "down"
                }`}
              ></i>
            </button>
            {activeDropdown === "people" && (
              <div className="mobile-filter-dropdown">
                <PeopleFilterDropdown
                  people={allPeople}
                  selectedPersonIds={filters.selectedPersonIds}
                  onChange={(v, clickedId) =>
                    applyFilter("selectedPersonIds", v, {
                      filterName: "people",
                      itemId: clickedId
                    })
                  }
                  onClose={() => {}}
                  isLoading={isLoading}
                  loadingItemId={loadingItemId}
                />
              </div>
            )}
          </div>
          )}

          {showPermissionsFilter && (
          <div className="mobile-filter-section">
            <button
              type="button"
              className={`mobile-filter-toggle${
                permissionsActive ? " mobile-filter-toggle--active" : ""
              }`}
              onClick={e => {
                e.stopPropagation();
                toggleDropdown(
                  activeDropdown === "permissions" ? null : "permissions"
                );
              }}
            >
              <span className="mobile-filter-toggle-label">
                PHOTO PERMISSIONS
                {permissionsActive && (
                  <span className="mobile-filter-badge">
                    {filters.photoPermissions.permissions.filter(
                      p => p.value !== "all"
                    ).length +
                      (filters.photoPermissions.redactedPhoto !== "all"
                        ? 1
                        : 0)}
                  </span>
                )}
              </span>
              <i
                className={`fa fa-chevron-${
                  activeDropdown === "permissions" ? "up" : "down"
                }`}
              ></i>
            </button>
            {activeDropdown === "permissions" && (
              <div className="mobile-filter-dropdown">
                <PhotoPermissionsDropdown
                  value={filters.photoPermissions}
                  onChange={(v, key) =>
                    applyFilter("photoPermissions", v, {
                      filterName: "permissions",
                      permissionKey: key
                    })
                  }
                  onClose={() => {}}
                  availableAttributes={availablePermissionAttributes}
                  showRedacted={hasRedactedParticipants}
                  isLoading={isLoading}
                  loadingKey={loadingPermissionKey}
                />
              </div>
            )}
          </div>
          )}

          <div className="mobile-filter-section">
            <button
              type="button"
              className={`mobile-filter-toggle${
                dateActive ? " mobile-filter-toggle--active" : ""
              }`}
              onClick={e => {
                e.stopPropagation();
                toggleDropdown(activeDropdown === "date" ? null : "date");
              }}
            >
              <span className="mobile-filter-toggle-label">
                DATE RANGE
                {dateActive && <span className="mobile-filter-badge">1</span>}
              </span>
              <i
                className={`fa fa-chevron-${
                  activeDropdown === "date" ? "up" : "down"
                }`}
              ></i>
            </button>
            {activeDropdown === "date" && (
              <div className="mobile-filter-dropdown">
                <DateRangeDropdown
                  value={filters.dateRange}
                  onChange={v =>
                    applyFilter("dateRange", v, { filterName: "date" })
                  }
                  onClose={() => {}}
                />
              </div>
            )}
          </div>

          <div className="mobile-filter-section">
            <button
              type="button"
              className={`mobile-filter-toggle mobile-filter-button${
                selfieActive ? " mobile-filter-toggle--active" : ""
              }`}
              onClick={e => {
                e.stopPropagation();
                onClose();
                toggleDropdown("selfie");
              }}
            >
              <span className="mobile-filter-toggle-label">
                {selfieActive && filters.selfie.imageUrl ? (
                  <img
                    src={filters.selfie.imageUrl}
                    alt="Selfie preview"
                    className="selfie-filter-preview"
                  />
                ) : (
                  <img
                    src={greySelfieIcon}
                    alt=""
                    width="14"
                    height="14"
                    className="selfie-icon"
                  />
                )}
                {selfieActive ? "CHANGE SELFIE" : "SELFIE SEARCH"}
              </span>
            </button>
          </div>

          <div className="mobile-filter-section">
            <button
              type="button"
              className={`mobile-filter-toggle${
                eventActive ? " mobile-filter-toggle--active" : ""
              }`}
              onClick={e => {
                e.stopPropagation();
                toggleDropdown(activeDropdown === "event" ? null : "event");
              }}
            >
              <span className="mobile-filter-toggle-label">
                SELECT EVENT
                {eventActive && (
                  <span className="mobile-filter-badge">
                    {filters.selectedEventIds.length}
                  </span>
                )}
              </span>
              <i
                className={`fa fa-chevron-${
                  activeDropdown === "event" ? "up" : "down"
                }`}
              ></i>
            </button>
            {activeDropdown === "event" && (
              <div className="mobile-filter-dropdown">
                <EventFilterDropdown
                  events={allEvents}
                  selectedEventIds={filters.selectedEventIds}
                  onChange={(v, clickedId) =>
                    applyFilter("selectedEventIds", v, {
                      filterName: "event",
                      itemId: clickedId
                    })
                  }
                  onClose={() => {}}
                  isLoading={isLoading}
                  loadingItemId={loadingItemId}
                />
              </div>
            )}
          </div>

          <div className="mobile-filter-section">
            <button
              type="button"
              className={`mobile-filter-toggle${
                tagActive ? " mobile-filter-toggle--active" : ""
              }`}
              onClick={e => {
                e.stopPropagation();
                toggleDropdown(activeDropdown === "tags" ? null : "tags");
              }}
            >
              <span className="mobile-filter-toggle-label">
                SELECT TAGS
                {tagActive && (
                  <span className="mobile-filter-badge">
                    {filters.selectedTagIds.length}
                  </span>
                )}
              </span>
              <i
                className={`fa fa-chevron-${
                  activeDropdown === "tags" ? "up" : "down"
                }`}
              ></i>
            </button>
            {activeDropdown === "tags" && (
              <div className="mobile-filter-dropdown">
                <TagFilterDropdown
                  tags={allTags}
                  selectedTagIds={filters.selectedTagIds}
                  onChange={(v, clickedId) =>
                    applyFilter("selectedTagIds", v, {
                      filterName: "tags",
                      itemId: clickedId
                    })
                  }
                  onClose={() => {}}
                  isLoading={isLoading}
                  loadingItemId={loadingItemId}
                />
              </div>
            )}
          </div>
        </div>

        <div className="mobile-filters-footer">
          <button
            type="button"
            className={`mobile-filters-apply-btn${
              activeFilterCount > 0 ? " mobile-filters-apply-btn--active" : ""
            }`}
            onClick={onClose}
          >
            APPLY FILTER{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MobileFiltersModal;
